import { Injectable } from '@angular/core';
import { LoggingApiService } from './logging-api.service';
import { HttpClient } from '@angular/common/http';
import { ErrorHash } from '@buildium/ngx-app-utility/dist';
import { BASE_PATH_API } from '@manager-base/app-base/environment/paths/constants';
import { Observable, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { JsErrorDto } from '@root/src/shared/domain/dtos/js-error.dto';

@Injectable()
export class FeatureFlagService {
    private featureFlagCache = new Map<string, Observable<boolean>>();

    keys = {
        AiScreeningServiceFlag: 'ai-screening-service-flag',
        AllianceBankServiceFlag: 'alliance-association-bank-service-flag',
        ApprovalService: 'approvals-service-flag',
        BankFeedServiceFlag: 'bank-feed-service-flag',
        BatchReportsService: 'buildium-batch-reports-service-flag',
        CalendarEventSeriesPatchingFlag: 'buildium-pt-13192-calendar-event-series-patching',
        CalendarsServiceFlag: 'calendars-service-flag',
        EnableCalendarEventRecurrenceEndDateDefaultsToOneYearFromStartDateFlag: 'buildium-pt-13224-enable-calendar-event-recurrence-end-date-defaults-to-one-year-from-start-date',
        EnableSanitizingCalendarEventDescriptionFlag: 'buildium-pt-12958-enable-sanitizing-calendar-event-description',
        ESignaturesServiceFlag: 'e-signatures-service-flag',
        ExternalApiSandboxServiceFlag: 'buildium-api-sandbox-service-flag',
        HaveIBeenPwnedService: 'haveibeenpwned-service-flag',
        InvoiceProcessingServiceFlag: 'invoice-processing-service-flag',
        KongApiService: 'kong-api-service-flag',
        LookerServiceFlag: 'buildium-looker-service-flag',
        OnsiteApiServiceFlag: 'onsite-api-service-flag',
        RemotePrintingServiceFlag: 'remote-printing-service-flag',
        RentersInsuranceService: 'renters-insurance-service-flag',
        ReportingServiceFlag: 'reporting-service-flag',
        ResidentFacingCalendarMilestone1Flag: 'buildium-pt-11816-resident-facing-calendar-milestone-1',
        ResidentFacingCalendarMilestone15Flag: 'buildium-pt-12198-resident-facing-calendar-milestone-1-5',
        ResidentFacingCalendarMilestone2Flag: 'buildium-pt-12198-resident-facing-calendar-milestone-2',
        ShareAbleForRentalsServiceFlag: 'shareable-for-rentals-service-flag',
        SnowflakeServiceFlag: 'buildium-snowflake-service-flag',
        S3ServiceFlag: 's3-service-flag',
        TwoFactorAuthenticationService: 'buildium-2fa-service-flag',
        TwoWayEmailCommunication: 'buildium-two-way-email-communication',
        UseSentryForManagerClient: 'buildium-use-sentry-for-manager-client',
        VirtualAssistantServiceFlag: 'buildium-virtual-assistant-service-flag',
        VirtualAssistantM2CommsFlag: 'buildium-virtual-assistant-m2-comms-feature-flag',
        WorkflowsFlag: 'buildium-workflows',
        BillApprovalActivityFeedNotificationFlag: 'buildium-bill-approval-activity-feed-notification',
        AmenityBookingFlag: 'buildium-pt-12391-amenity-booking'
    };

    constructor(
        private loggingApiService: LoggingApiService,
        private http: HttpClient,
        private errorHash: ErrorHash
    ) {
    }

    retrieveFlag(key: string): Observable<boolean> {
        return this.retrieveFlagCommon(key);
    }

    retrievePublicFlag(key: string): Observable<boolean> {
        return this.retrieveFlagCommon(key, true);
    }

    private retrieveFlagCommon(key: string, isPublic: boolean = false): Observable<boolean> {
        if (!key) {
            const message = 'No feature flag found.';
            const hash = this.errorHash.hashErrorString(message);
            const jsErrorDto: JsErrorDto = {
                message,
                location: window.location.pathname,
                userAgent: window.navigator.userAgent,
                hash,
                callstack: ''
            };
            this.loggingApiService.logError(jsErrorDto);
            return of(false);
        }

        if (!this.featureFlagCache.has(key)) {
            const url = `${BASE_PATH_API}/${isPublic ? 'public' : 'external'}/featureFlags/${key}`;
            const observable = this.http.get<boolean>(url).pipe(
                shareReplay(1)
            );
            this.featureFlagCache.set(key, observable);
        }

        return this.featureFlagCache.get(key);
    }

    isOverrideEnabled(): Observable<boolean> {
        const url = `${BASE_PATH_API}/public/FeatureFlags/settings/overrides/validity`;
        return this.http.get<boolean>(url);
    }
}
